<template>
  <Modal
    ref="modal"
    size="sm"
    @close="$emit('close')"
  >
    <template #icon>
      <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
    />
</svg>
    </template>

    <template #header>
      Assign WBS Code
    </template>

    <template #body>
      <div class="space-y-4">
        <p class="text-gray-600">
          Select a WBS code to assign to all trade items in this package.
        </p>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            WBS Code
          </label>

          <AppTypeAhead
            v-model="wbsCodeSearch"
            name="wbs_code"
            class="form-input"
            :suggestion-callback="getWbsCodeSuggestions"
            :initial-suggestions="getWbsCodeSuggestions"
            :error="error"
            placeholder="Search for WBS code..."
            @value-was-selected="selectWbsCode"
          />
        </div>

        <div
          v-if="selectedWbsCode"
          class="bg-blue-50 border border-blue-200 rounded-lg p-3"
        >
          <div class="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-blue-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

            <span class="text-sm font-medium text-blue-900">
              Selected: {{ selectedWbsCode.code }} ({{ selectedWbsCode.unit }}) - {{ selectedWbsCode.description }}
            </span>
          </div>
        </div>

        <div
          v-if="invalidPackTradeItems.length > 0"
          class="bg-red-50 border border-red-200 rounded-lg p-3"
        >
          <span class="text-sm font-medium text-red-900">
            The following package items have a different unit to the selected WBS code and will not be assigned:
            <ul class="list-disc list-inside">
              <li
                v-for="item in invalidPackTradeItems"
                :key="item.id"
              >
                {{ item.trade_item.name }} ({{ item.trade_item.unit }})
              </li>
            </ul>
          </span>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-end space-x-2 w-full">
        <button
          type="button"
          class="btn--secondary-default btn"
          @click="modal.close"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn btn--primary-blue flex items-center space-x-2"
          :disabled="!selectedWbsCode || isAssigning"
          @click="assignWbsCode"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

          <span>{{ isAssigning ? 'Assigning...' : 'Assign WBS Code' }}</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Package from '@/models/Package';
import PackageTradeItem from '@/models/PackageTradeItem';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
  packageId: Id;
  package: Package;
  packageTradeItems: PackageTradeItem[];
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'assigned');
}>();

const modal = useTemplateRef('modal');
const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const wbsCodeSearch = ref('');
const selectedWbsCodeId = ref<Id>(undefined);
const error = ref('');
const assignWbsCodeAction = useStoreApiAction(packageTradeItemsStore.assignWbsCode);
const isAssigning = computed(() => assignWbsCodeAction.is(States.LOADING));

const filteredWbsCodes = computed(() => {
  const searchTerm = wbsCodeSearch.value.toLowerCase();

  let wbsCodes = wbsCodesStore.models
    .where('project_id', projectsStore.project.id);

  if(searchTerm.trim()) {
    wbsCodes = wbsCodes
      .where('code', (code) => code.toLowerCase().includes(searchTerm))
      .orWhere('description', (description) => description.toLowerCase().includes(searchTerm));
  }

  return wbsCodes.get();
});

const selectedWbsCode = computed(() => {
  if(!selectedWbsCodeId.value) return undefined;

  return wbsCodesStore.models.find(selectedWbsCodeId.value);
});

const invalidPackTradeItems = computed(() => {
  if(!selectedWbsCode.value || props.package.procurement_method === 'lump_sum') return [];

  return props.packageTradeItems.filter((packageTradeItem) =>
    packageTradeItem.trade_item.unit !== selectedWbsCode.value?.unit
  );
});

const getWbsCodeSuggestions = () => {
  return filteredWbsCodes.value.map((wbsCode) => ({
    label: `${wbsCode.code} - ${wbsCode.description}`,
    value: wbsCode.id,
  }));
};

const getInitialWbsCodeSuggestions = () => {
  return filteredWbsCodes.value
    .map((wbsCode) => ({
      label: `${wbsCode.code} - ${wbsCode.description}`,
      value: wbsCode.id,
    }));
};

const selectWbsCode = ({ index, value }: { index: number; value: { label: string; value: unknown } }) => {
  selectedWbsCodeId.value = value.value as Id;
  error.value = '';
};

const assignWbsCode = () => {
  if(!selectedWbsCodeId.value) {
    error.value = 'Please select a WBS code';

    return;
  }

  error.value = '';

  assignWbsCodeAction.request(props.packageId, {
    wbs_code_id: Number(selectedWbsCodeId.value),
    package_trade_item_ids: props.packageTradeItems.map((item) => item.id),
  }).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'WBS Code Assigned',
      text: 'WBS code has been successfully assigned to all trade items.',
    });

    // Reset form
    wbsCodeSearch.value = '';
    selectedWbsCodeId.value = undefined;
    error.value = '';

    emits('assigned');
    emits('close');
  }).catch((error) => {
    console.error('Failed to assign WBS code:', error);

    Swal.fire({
      icon: 'error',
      title: 'Failed to assign WBS code',
      text: 'There was an error assigning the WBS code to trade items. Please try again.',
    });
  });
};

// Reset form when component is mounted
watch(() => props.packageId, () => {
  wbsCodeSearch.value = '';
  selectedWbsCodeId.value = undefined;
  error.value = '';
}, { immediate: true });
</script>
